import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    Typography,
    Button,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    CircularProgress,
    TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import styled from '@mui/system/styled';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useOcrJobs } from '../../Contexts/OcrJobsContext';
import {
    getAllowedTransferTypes,
    downloadDocumentFiles,
    buildTransferDto,
    enqueueIncomesExpensesOcr,
    deleteSourceDocument,
    collectDocumentUrls,
    translateClarificationError,
} from './DocumentTransferUtils';
import ClarificationDocumentService from '../../Services/clarificationDocuments.service';

const MySwal = withReactContent(Swal);

const StyledDialog = styled(Dialog)({
    '& .MuiPaper-root': {
        width: '28%',
        maxWidth: '420px',
        minWidth: '320px',
    },
});

const StyledDialogTitle = styled(DialogTitle)({
    fontSize: '1.5rem',
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#025366',
});

const StyledDialogContent = styled(DialogContent)({
    textAlign: 'center',
    padding: '20px 30px',
});

const StyledDialogActions = styled(DialogActions)({
    justifyContent: 'center',
    paddingBottom: '20px',
    gap: '8px',
});

const OptionButton = styled(Button)({
    margin: '10px 0',
    padding: '12px 25px',
    fontSize: '1.1rem',
    textTransform: 'none',
    color: '#025366',
    backgroundColor: '#f5f5f5',
    border: '1px solid #E57C22',
    width: '100%',
    maxWidth: '220px',
    '&:hover': {
        backgroundColor: '#E57C22',
        color: '#fff',
    },
});

const ConfirmButton = styled(Button)({
    margin: '0 8px',
    padding: '10px 20px',
    fontSize: '1.1rem',
    textTransform: 'none',
    color: '#fff',
    backgroundColor: '#4caf50',
    '&:hover': {
        backgroundColor: '#388e3c',
    },
    '&.Mui-disabled': {
        backgroundColor: '#9e9e9e',
        color: '#fff',
    },
});

const CancelButton = styled(Button)({
    margin: '0 8px',
    padding: '10px 20px',
    fontSize: '1.1rem',
    textTransform: 'none',
    color: '#025366',
    border: '1px solid #025366',
});

const askDeleteSources = async (t) => {
    const result = await MySwal.fire({
        title: t('DeleteSourceDocuments'),
        text: t('DoYouWantToDeleteTheSourceDocuments'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: t('YesDeleteSourceDocuments'),
        cancelButtonText: t('NoKeepSourceDocuments'),
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
    });
    return result.isConfirmed;
};

/**
 * Prepare transfer items: download files and build DTOs per source document.
 * Throws on first download failure (caller reports which document failed).
 */
const prepareTransferItems = async (selectedDocuments, client, toType, onProgress) => {
    const items = [];

    for (let i = 0; i < selectedDocuments.length; i++) {
        const document = selectedDocuments[i];
        if (onProgress) {
            onProgress(i + 1, selectedDocuments.length, 'download');
        }

        const urls = collectDocumentUrls(document);
        if (!urls.length) {
            const err = new Error(`No transferable files for document ${document.id}`);
            err.documentId = document.id;
            err.code = 'NO_URLS';
            throw err;
        }

        const files = await downloadDocumentFiles(document, { useUniqueNames: true });
        items.push({
            sourceDocument: document,
            sourceId: document.id,
            fromType: document.docType,
            files,
            dto: buildTransferDto(document, client, toType),
        });
    }

    return items;
};

const MultiDocumentTransferDialog = ({
    open,
    selectedDocuments = [],
    fromType,
    token,
    client,
    setLoading,
    RefreshPage,
    onClose,
    onTransferComplete,
    t,
}) => {
    const navigate = useNavigate();
    const { addJobs } = useOcrJobs();

    const [step, setStep] = useState('type'); // type | confirm | result
    const [selectedType, setSelectedType] = useState('');
    const [clarificationNote, setClarificationNote] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [progressLabel, setProgressLabel] = useState('');
    const [batchResults, setBatchResults] = useState(null);

    const allowedTypes = useMemo(() => getAllowedTransferTypes(fromType), [fromType]);
    const count = selectedDocuments.length;

    useEffect(() => {
        if (open) {
            setStep('type');
            setSelectedType('');
            setIsProcessing(false);
            setProgressLabel('');
            setBatchResults(null);
        }
    }, [open]);

    const handleClose = () => {
        if (isProcessing) return;
        if (onClose) onClose();
    };

    const handleTypeSelection = (type) => {
        if (isProcessing) return;
        setSelectedType(type);
        setStep('confirm');
    };

    const showSummaryToast = (results) => {
        const successCount = results.filter((r) => r.success).length;
        const failCount = results.length - successCount;
        const deletedCount = results.filter((r) => r.deleted).length;
        toast.info(
            t('MultiTransferSummary', {
                selected: results.length,
                success: successCount,
                failed: failCount,
                deleted: deletedCount,
            }),
            { autoClose: 8000 }
        );
        if (failCount > 0) {
            const failedIds = results.filter((r) => !r.success).map((r) => r.sourceId).join(', ');
            toast.error(t('MultiTransferFailedDocuments', { ids: failedIds }), { autoClose: 10000 });
        }
    };

    const finishWithResults = async (results) => {
        setBatchResults(results);
        showSummaryToast(results);

        const anySuccess = results.some((r) => r.success);
        if (anySuccess) {
            await RefreshPage?.();
            setStep('result');
        } else {
            // Keep dialog open on total failure; stay on confirm so user can retry/cancel
            setStep('confirm');
        }
    };

    const handleConfirm = async () => {
        if (isProcessing || !selectedType) return;

        const sourceTypes = new Set(
            selectedDocuments.map((d) => d.docType).filter(Boolean)
        );
        if (sourceTypes.size > 1) {
            toast.error(t('CannotTransferMixedDocumentTypes'));
            return;
        }
        if (sourceTypes.size === 1 && ![...sourceTypes][0]) {
            toast.error(t('CannotTransferMixedDocumentTypes'));
            return;
        }
        if (fromType && [...sourceTypes][0] && [...sourceTypes][0] !== fromType) {
            toast.error(t('CannotTransferMixedDocumentTypes'));
            return;
        }
        if (!count) {
            toast.warning(t('SelectAtLeastOneDocumentToTransfer'));
            return;
        }

        setIsProcessing(true);
        setLoading?.(true);

        try {
            // ── Clarification: server-side snapshot move (no PDF download / OCR) ──
            if (selectedType === 'DocumentsRequiringClarification') {
                const results = [];
                for (let i = 0; i < selectedDocuments.length; i++) {
                    const doc = selectedDocuments[i];
                    setProgressLabel(
                        t('TransferringDocumentProgress', {
                            current: i + 1,
                            total: selectedDocuments.length,
                        })
                    );
                    try {
                        const response = await ClarificationDocumentService.MoveToClarification({
                            caseId: client.caseId,
                            accountantId: client.accountantId,
                            fromType: doc.docType || fromType,
                            id: doc.id,
                            clarificationNote: clarificationNote?.trim() || null,
                        }, token);
                        if (response.ok) {
                            results.push({
                                sourceId: doc.id,
                                success: true,
                                jobIds: [],
                                deleted: true,
                                error: null,
                            });
                        } else {
                            const errorText = await response.text();
                            results.push({
                                sourceId: doc.id,
                                success: false,
                                jobIds: [],
                                deleted: false,
                                error: translateClarificationError(t, errorText) || 'move_failed',
                            });
                        }
                    } catch (err) {
                        results.push({
                            sourceId: doc.id,
                            success: false,
                            jobIds: [],
                            deleted: false,
                            error: err?.message || 'move_failed',
                        });
                    }
                }
                await finishWithResults(results);
                if (onTransferComplete) {
                    onTransferComplete({ results });
                }
                return;
            }

            let transferItems;
            try {
                transferItems = await prepareTransferItems(
                    selectedDocuments,
                    client,
                    selectedType,
                    (current, total) => {
                        setProgressLabel(
                            t('TransferringDocumentProgress', { current, total })
                        );
                    }
                );
            } catch (downloadError) {
                toast.error(
                    t('MultiTransferDownloadFailed', {
                        id: downloadError.documentId || '?',
                    })
                );
                return;
            }

            const wantDelete = await askDeleteSources(t);

            // ── Incomes / Expenses: sequential OCR enqueue per source ──
            if (selectedType === 'Incomes' || selectedType === 'Expenses') {
                const results = [];
                const allJobIds = [];

                for (let i = 0; i < transferItems.length; i++) {
                    const item = transferItems[i];
                    setProgressLabel(
                        t('TransferringDocumentProgress', {
                            current: i + 1,
                            total: transferItems.length,
                        })
                    );

                    const jobIds = await enqueueIncomesExpensesOcr(
                        item.dto,
                        item.files,
                        token,
                        t,
                        null // suppress per-item toast; show batch summary
                    );

                    if (jobIds && jobIds.length > 0) {
                        allJobIds.push(...jobIds);
                        let deleted = false;
                        if (wantDelete) {
                            deleted = await deleteSourceDocument(
                                item.fromType,
                                item.sourceId,
                                token,
                                t,
                                null
                            );
                        }
                        results.push({
                            sourceId: item.sourceId,
                            success: true,
                            jobIds,
                            deleted,
                            error: null,
                        });
                    } else {
                        results.push({
                            sourceId: item.sourceId,
                            success: false,
                            jobIds: [],
                            deleted: false,
                            error: 'upload_failed',
                        });
                    }
                }

                if (allJobIds.length > 0) {
                    const firstDto = transferItems[0]?.dto;
                    const reportingDate = firstDto?.reportingMonthDate
                        ? new Date(firstDto.reportingMonthDate)
                        : new Date();
                    addJobs(allJobIds, 'ocr', async () => {
                        await RefreshPage?.();
                    }, {
                        caseId: firstDto?.caseId || client?.caseId,
                        clientName: client?.name || '',
                        reportingMonth: `${reportingDate.getMonth() + 1}/${reportingDate.getFullYear()}`,
                        fileCount: transferItems.reduce((n, it) => n + it.files.length, 0),
                    });
                }

                await finishWithResults(results);
                return;
            }

            // ── PaymentAcceptances: navigate with multi-transfer state ──
            if (selectedType === 'PaymentAcceptances') {
                sessionStorage.removeItem('hasTransferred');
                sessionStorage.removeItem('hasMultiTransferred');

                navigate('/PaymentAcceptance', {
                    state: {
                        type: 'PaymentAcceptances',
                        client,
                        isMultiTransfer: true,
                        transferItems: transferItems.map((item) => ({
                            files: item.files,
                            dto: item.dto,
                            idToDelete: wantDelete ? item.sourceId : null,
                            fileTypeToDelete: wantDelete ? item.fromType : null,
                        })),
                        requiresTransferDateSelection: true,
                    },
                });

                if (onTransferComplete) {
                    onTransferComplete({ results: [], navigated: true });
                }
                return;
            }

            // ── OtherDocuments: navigate with multi-transfer state ──
            if (selectedType === 'OtherDocuments') {
                sessionStorage.removeItem('hasTransferredOtherDocuments');
                sessionStorage.removeItem('hasMultiTransferredOtherDocuments');

                navigate('/OtherDocument', {
                    state: {
                        type: 'OtherDocuments',
                        client,
                        isMultiTransfer: true,
                        transferItems: transferItems.map((item) => ({
                            files: item.files,
                            dto: item.dto,
                            idToDelete: wantDelete ? item.sourceId : null,
                            fileTypeToDelete: wantDelete ? item.fromType : null,
                        })),
                    },
                });

                if (onTransferComplete) {
                    onTransferComplete({ results: [], navigated: true });
                }
            }
        } catch (error) {
            console.error('Error during multi document transfer:', error);
            toast.error(t('documentUploadError', { errorText: error.message || '' }));
        } finally {
            setIsProcessing(false);
            setLoading?.(false);
            setProgressLabel('');
        }
    };

    const handleResultClose = () => {
        if (onTransferComplete && batchResults) {
            onTransferComplete({
                results: batchResults,
                failedIds: batchResults.filter((r) => !r.success).map((r) => r.sourceId),
            });
        }
        if (onClose) onClose();
    };

    if (!open) return null;

    return (
        <>
            <StyledDialog
                open={open && step === 'type'}
                onClose={handleClose}
                disableEscapeKeyDown={isProcessing}
            >
                <StyledDialogTitle>{t('MultiTransferDocuments')}</StyledDialogTitle>
                <StyledDialogContent>
                    <Typography variant="body2" color="#025366" sx={{ mb: 1 }}>
                        {t('SelectedDocumentsCount', { count })}
                    </Typography>
                    <Typography variant="body2" color="#025366" sx={{ mb: 1 }}>
                        {t('SourceDocumentType')}: {t(fromType)}
                    </Typography>
                    <Typography variant="body2" color="#025366" sx={{ mb: 2 }}>
                        {t('ChooseTypeToTransferDocument')}:
                    </Typography>
                    <Box display="flex" flexDirection="column" alignItems="center">
                        {allowedTypes.map((type) => (
                            <OptionButton
                                key={type}
                                onClick={() => handleTypeSelection(type)}
                                disabled={isProcessing}
                            >
                                {t(type)}
                            </OptionButton>
                        ))}
                    </Box>
                </StyledDialogContent>
                <StyledDialogActions>
                    <CancelButton onClick={handleClose} disabled={isProcessing}>
                        {t('Cancel')}
                    </CancelButton>
                </StyledDialogActions>
            </StyledDialog>

            <Dialog
                open={open && step === 'confirm'}
                onClose={isProcessing ? undefined : handleClose}
                disableEscapeKeyDown={isProcessing}
            >
                <StyledDialogTitle>{t('ConfirmTransfer')}</StyledDialogTitle>
                <StyledDialogContent>
                    <Typography variant="body1" color="#025366" sx={{ mb: 1 }}>
                        {t('ConfirmMultiTransfer', {
                            count,
                            fromType: t(fromType),
                            toType: t(selectedType),
                        })}
                    </Typography>
                    {selectedType === 'DocumentsRequiringClarification' && !isProcessing && (
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            margin="normal"
                            label={t('ClarificationNote')}
                            placeholder={t('ClarificationNotePlaceholder')}
                            value={clarificationNote}
                            onChange={(e) => setClarificationNote(e.target.value)}
                        />
                    )}
                    {isProcessing && (
                        <Box display="flex" flexDirection="column" alignItems="center" mt={2} gap={1}>
                            <CircularProgress size={32} />
                            {progressLabel && (
                                <Typography variant="body2" color="#025366">
                                    {progressLabel}
                                </Typography>
                            )}
                        </Box>
                    )}
                </StyledDialogContent>
                <StyledDialogActions>
                    <CancelButton onClick={handleClose} disabled={isProcessing}>
                        {t('Cancel')}
                    </CancelButton>
                    <ConfirmButton
                        onClick={handleConfirm}
                        disabled={isProcessing}
                        startIcon={<TransferWithinAStationIcon style={{ marginRight: '10px' }} />}
                    >
                        {t('Confirm')}
                    </ConfirmButton>
                </StyledDialogActions>
            </Dialog>

            <Dialog open={open && step === 'result'} onClose={handleResultClose}>
                <StyledDialogTitle>{t('ConfirmTransfer')}</StyledDialogTitle>
                <StyledDialogContent>
                    {batchResults && (
                        <Typography variant="body1" color="#025366">
                            {t('MultiTransferSummary', {
                                selected: batchResults.length,
                                success: batchResults.filter((r) => r.success).length,
                                failed: batchResults.filter((r) => !r.success).length,
                                deleted: batchResults.filter((r) => r.deleted).length,
                            })}
                        </Typography>
                    )}
                </StyledDialogContent>
                <StyledDialogActions>
                    <ConfirmButton onClick={handleResultClose}>
                        {t('close') || t('Confirm')}
                    </ConfirmButton>
                </StyledDialogActions>
            </Dialog>
        </>
    );
};

export default MultiDocumentTransferDialog;
